import React from 'react';
import styled from '@emotion/styled';
import type {
  ColorProps,
  FontSizeProps,
  LineHeightProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import { borderRadius, color, display, fontSize, lineHeight, space, width } from 'styled-system';

import { Button } from 'common/components/Button';
import type { FlexProps } from 'common/components/Flex';
import { Flex } from 'common/components/Flex';
import { useLocalizedDate } from 'common/hooks/date';
import { useLocalization } from 'common/providers/LocaleProvider';
import { getMaskedCode } from 'common/utils/gift-cards';
import type { GiftCardStatus as GiftCardStatusType } from '../types/gift-card';
import { Text, Title } from './Text';

interface GiftCardStatusProps extends GiftCardStatusType {
  onRestartButtonClick: () => void;
}

const Container = styled(Flex)<FlexProps>``;
const containerStyles: FlexProps = {
  width: [1, '320px'],
  flexDirection: 'column',
};

const TextContainer = styled(Flex)<FlexProps>``;
const textContainerStyles: FlexProps = {
  mb: ['sp_20', 'sp_16'],
  alignItems: 'center',
  justifyContent: ['flex-start', 'center'],
};

const RestartButton = styled(Button)<WidthProps & SpaceProps & FontSizeProps & LineHeightProps>`
  ${width}
  ${space}
  ${fontSize}
  ${lineHeight}
`;
const restartButtonStyles = {
  width: 1,
  mb: 'sp_16',
  fontSize: 'fs_16',
  lineHeight: 'lh_24',
};

const GreenBoxText = styled(Flex)<FlexProps & ColorProps>`
  ${display}
  ${space}
  ${color}
  ${borderRadius}
  ${fontSize}
  ${lineHeight}
`;
const greenBoxTextStyles = {
  p: 'sp_4',
  display: 'inline',
  borderRadius: 'medium',
  fontSize: ['fs_14', null, 'fs_16'],
  lineHeight: ['lh_20', null, 'fs_24'],
  color: 'sys.success.text.default',
  bg: 'sys.success.background.weak',
} as FlexProps;

const GiftCardStatus = ({
  maskedCode,
  balance,
  validUntil,
  onRestartButtonClick,
}: GiftCardStatusProps) => {
  const { t, fc } = useLocalization();
  const formattedDate = useLocalizedDate(validUntil);

  return (
    <>
      <Title>{t('h24_gc_status_title')}</Title>
      <Container {...containerStyles}>
        <Text>
          {t('h24_check_balance_gc_number')}: <b>{getMaskedCode(maskedCode)}</b>
        </Text>
        <TextContainer data-testid="balance" {...textContainerStyles}>
          <Text mb="sp_0" mr="sp_4">
            {t('h24_check_balance_gc_remaning_credit')}:
          </Text>
          <GreenBoxText {...greenBoxTextStyles}>
            <b>{fc(balance)}</b>
          </GreenBoxText>
        </TextContainer>
        <Text>
          {`${t('h24_check_balance_gc_valid_until')}: `}
          <b>{formattedDate}</b>
        </Text>
        <RestartButton onClick={onRestartButtonClick} {...restartButtonStyles}>
          {t('h24_check_balance_gc_restart_btn')}
        </RestartButton>
      </Container>
    </>
  );
};

export default GiftCardStatus;
